import React, { useMemo, useState } from 'react';
import { ItemType, MoodboardItem } from '../types/moodboard';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from './ui/dialog';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Label } from './ui/label';
import { Plus } from 'lucide-react';
interface AddItemDialogProps {
  onAdd: (item: Omit<MoodboardItem, 'id' | 'createdAt'>) => void;
  existingTags?: string[];
}
export function AddItemDialog({
  onAdd,
  existingTags = []
}: AddItemDialogProps) {
  const [open, setOpen] = useState(false);
  const [type, setType] = useState<ItemType>('image');
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [description, setDescription] = useState('');
  const [tagsInput, setTagsInput] = useState('');
  // Get unique tag suggestions sorted by frequency
  const tagSuggestions = useMemo(() => {
    const tagCounts = existingTags.reduce((acc, tag) => {
      const upper = tag.toUpperCase();
      acc[upper] = (acc[upper] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
    return Object.entries(tagCounts).sort((a, b) => b[1] - a[1]).slice(0, 8).map(([tag]) => tag);
  }, [existingTags]);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!content.trim()) return;
    const tags = tagsInput.split(',').map(t => t.trim().toUpperCase()).filter(t => t.length > 0);
    onAdd({
      type,
      title: title.trim().toUpperCase(),
      content: content.trim(),
      description: description.trim(),
      tags
    });
    setType('image');
    setTitle('');
    setContent('');
    setDescription('');
    setTagsInput('');
    setOpen(false);
  };
  const addSuggestedTag = (tag: string) => {
    const currentTags = tagsInput.split(',').map(t => t.trim().toUpperCase()).filter(t => t.length > 0);
    if (!currentTags.includes(tag)) {
      const newTags = [...currentTags, tag].join(', ');
      setTagsInput(newTags);
    }
  };
  const getContentLabel = () => {
    switch (type) {
      case 'image':
        return 'URL IMMAGINE';
      case 'link':
        return 'URL SITO WEB';
      case 'note':
        return 'TESTO DELLA NOTA';
      case 'color_palette':
        return 'COLORI (HEX, SEPARATI DA VIRGOLA)';
      default:
        return 'CONTENUTO';
    }
  };
  return <Dialog open={open} onOpenChange={setOpen} data-id="element-37">
      <DialogTrigger asChild data-id="element-38">
        <button className="text-[#999] hover:text-black transition-colors" aria-label="Nuovo Elemento" data-id="element-39">
          <Plus className="w-[18px] h-[18px]" strokeWidth={1.5} data-id="element-40" />
        </button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[480px] bg-white border border-border rounded-xl p-0 shadow-none w-[calc(100%-2rem)] mx-auto max-h-[90vh] overflow-y-auto" data-id="element-41">
        <DialogHeader className="px-6 sm:px-8 pt-6 sm:pt-8 pb-4 sm:pb-6 border-b border-border" data-id="element-42">
          <DialogTitle className="font-heading font-bold text-[14px] tracking-[0.25em] uppercase" data-id="element-43">
            AGGIUNGI ELEMENTO
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="px-6 sm:px-8 py-6 space-y-6" data-id="element-44">
          <div className="space-y-3" data-id="element-45">
            <Select value={type} onValueChange={value => setType(value as ItemType)} data-id="element-46">
              <SelectTrigger className="rounded-full border-border bg-white text-[12px] font-sans h-10 px-4" data-id="element-47">
                <SelectValue placeholder="Seleziona tipo" data-id="element-48" />
              </SelectTrigger>
              <SelectContent className="rounded-xl border-border bg-white" data-id="element-49">
                <SelectItem value="image" className="text-[12px] font-sans" data-id="element-50">
                  Immagine
                </SelectItem>
                <SelectItem value="note" className="text-[12px] font-sans" data-id="element-51">
                  Nota
                </SelectItem>
                <SelectItem value="color_palette" className="text-[12px] font-sans" data-id="element-52">
                  Palette Colori
                </SelectItem>
                <SelectItem value="link" className="text-[12px] font-sans" data-id="element-53">
                  Link
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {type !== 'note' && type !== 'color_palette' && <div className="space-y-3" data-id="element-54">
              <Label className="font-heading text-[10px] tracking-[0.2em] uppercase text-[#666]" data-id="element-55">
                Titolo (Opzionale)
              </Label>
              <Input value={title} onChange={e => setTitle(e.target.value)} className="rounded-full border-border bg-white text-[13px] font-sans h-10 px-4" data-id="element-56" />
            </div>}

          <div className="space-y-3" data-id="element-57">
            <Label className="font-heading text-[10px] tracking-[0.2em] uppercase text-[#666]" data-id="element-58">
              {type === 'image' ? 'URL Immagine' : type === 'link' ? 'URL Link' : type === 'color_palette' ? 'Colori (es. #000000, #FFFFFF)' : 'Contenuto'}
            </Label>
            {type === 'note' ? <Textarea value={content} onChange={e => setContent(e.target.value)} required className="min-h-[100px] rounded-2xl border-border bg-white text-[13px] font-sans p-4" data-id="element-59" /> : <Input value={content} onChange={e => setContent(e.target.value)} required className="rounded-full border-border bg-white text-[13px] font-sans h-10 px-4" data-id="element-60" />}
          </div>

          {type !== 'note' && type !== 'color_palette' && <div className="space-y-3" data-id="element-61">
              <Label className="font-heading text-[10px] tracking-[0.2em] uppercase text-[#666]" data-id="element-62">
                Descrizione (Opzionale)
              </Label>
              <Textarea value={description} onChange={e => setDescription(e.target.value)} className="h-20 rounded-2xl border-border bg-white text-[13px] font-sans p-4" data-id="element-63" />
            </div>}

          <div className="space-y-3" data-id="element-64">
            <Label className="font-heading text-[10px] tracking-[0.2em] uppercase text-[#666]" data-id="element-65">
              Tag (Separati da virgola)
            </Label>
            <Input value={tagsInput} onChange={e => setTagsInput(e.target.value)} className="rounded-full border-border bg-white text-[13px] font-sans h-10 px-4" placeholder="es. SARTORIA, LAYERING, FW25" data-id="element-66" />
            {tagSuggestions.length > 0 && <div className="flex flex-wrap gap-2 pt-2" data-id="element-67">
                {tagSuggestions.map(tag => <button key={tag} type="button" onClick={() => addSuggestedTag(tag)} className="font-heading text-[9px] tracking-[0.15em] uppercase text-[#666] bg-[#F5F5F5] px-3 py-1.5 rounded-full hover:bg-[#E5E5E5] hover:text-black transition-colors" data-id="element-68">
                    + {tag}
                  </button>)}
              </div>}
          </div>

          <DialogFooter className="pt-6 border-t border-border flex gap-4" data-id="element-69">
            <button type="button" onClick={() => setOpen(false)} className="font-heading text-[11px] tracking-[0.15em] uppercase text-[#666] hover:text-black transition-colors" data-id="element-70">
              ANNULLA
            </button>
            <button type="submit" disabled={!content.trim()} className="font-heading text-[11px] tracking-[0.15em] uppercase text-black border-b border-black pb-0.5 hover:opacity-60 transition-opacity disabled:opacity-30" data-id="element-71">
              AGGIUNGI ALLA BOARD
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>;
}